import { useQuery } from 'react-query'

const { REACT_APP_PANOPTICON_CAN_SEND_BASE } = process.env

export interface PermissionEvent {
  PK: string
  SK: string
  recordType: 'PERMISSION_CHECK'
  timestamp: string
  userId: string
  chatId: string
  senderNumber: string
  recipientNumber: string
  currentCanSend: boolean
  panopticonCanSend: boolean
  finalDecision: boolean
  reason: string
  unrepliedCount: number
  responseRate: number
  hasRepliedToday: boolean
  hasEverReplied: boolean
  initiatedByMe: boolean
  lastSent?: string
  lastReceived?: string
  dailyChatLimit: number
  maxUnrepliedLimit: number
  minResponseRate: number
  graceUsedToday: number
  graceLeftToday: number
  totalResponseLimitedMessages: number
  totalHistoricalRecipients: number
  totalActiveToday: number
  newChatsToday: number
  currentBlockLevel: number
  panopticonResponse: any
  phoneNumber: string
  hourBucket: string
  dayBucket: string
  ttl: number
  createdAt: string
}

export interface PermissionEventFilters {
  recipientNumber?: string
  finalDecision?: boolean
  panopticonCanSend?: boolean
  startDate?: string
  endDate?: string
  limit?: number
  cursor?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    nextCursor?: string
    hasMore: boolean
    currentPage: number
    itemsPerPage: number
  }
}

const fetchPermissionEvents = async (
  senderNumber: string,
  filters: PermissionEventFilters = {}
): Promise<
  {
    success: boolean
    senderNumber: string
  } & PaginatedResponse<PermissionEvent>
> => {
  const encodedSender = encodeURIComponent(senderNumber)
  const params = new URLSearchParams()

  // Add filters as query parameters
  if (filters.recipientNumber)
    params.append('recipientNumber', filters.recipientNumber)
  if (filters.finalDecision !== undefined)
    params.append('finalDecision', filters.finalDecision.toString())
  if (filters.panopticonCanSend !== undefined)
    params.append('panopticonCanSend', filters.panopticonCanSend.toString())
  if (filters.startDate) params.append('startDate', filters.startDate)
  if (filters.endDate) {
    // Make end date inclusive by adding time to end of day
    const endDate = new Date(`${filters.endDate}T23:59:59.999Z`)
    params.append('endDate', endDate.toISOString())
  }
  if (filters.limit) params.append('limit', filters.limit.toString())
  if (filters.cursor) params.append('cursor', filters.cursor)

  const url = `${REACT_APP_PANOPTICON_CAN_SEND_BASE}/permission-events/senders/${encodedSender}?${params}`

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

export const usePermissionEvents = (
  senderNumber: string,
  filters: PermissionEventFilters = {},
  enabled = true
) => {
  return useQuery(
    ['permission-events', senderNumber, filters],
    () => fetchPermissionEvents(senderNumber, filters),
    {
      enabled: enabled && !!senderNumber,
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
    }
  )
}

export default usePermissionEvents
